import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout } from "../../components"

/* Widgets */
import { CardForm } from "../../widgets/Card"

/* REST API */
import { list } from "../../api/Card"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Constants */
import { env } from "../../constants"

/* Icons */
import { EditIcon, DeckIcon, LayersIcon, CheckIcon, WarningIcon, BarcodeIcon, HeartIcon, DiamondIcon, ClubIcon, SpadeIcon } from "../../widgets/Card/CardIcons"

/* Utils */
import { SUITS, getSuit, getSuitName, getRank, getCode, getDoubles, getState, formatDate } from "../../widgets/Card/CardUtils"

/* Translate */
import { t } from "../../translates/translate"


/* Page Cards */
class Cards extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            stats: null,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    /* Load Action */
    load = (reload = false) => {

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list().then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, stats: response.data.stats })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], stats: null })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], stats: null })
            }
            else {
                this.setState({ status: "error", data: [], stats: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], stats: null })
        })

    }

    /* Suits Of The Deck — the whole deck arrives at once, so the count stays here */
    getSuits = () => {

        /* Fields */
        const { data } = this.state
        const suits = SUITS.filter((suit) => data.some((item) => getSuit(item) === suit))

        /* Cards Of An Unknown Suit — the deck comes from a seeder, but a renamed row must not disappear */
        if (data.some((item) => getSuit(item) === "other")) {
            return [...suits, "other"]
        }

        /* Send Result */
        return suits
    }

    /* Draw Suit Icon */
    _suit = (suit, size) => {

        /* Check Suit */
        if (suit === "heart") {
            return <HeartIcon size={size} />
        }

        if (suit === "diamond") {
            return <DiamondIcon size={size} />
        }

        if (suit === "club") {
            return <ClubIcon size={size} />
        }

        if (suit === "spade") {
            return <SpadeIcon size={size} />
        }

        /* Send Result */
        return <DeckIcon size={size} />
    }

    /* Draw Summary — what the deck really holds */
    _summary = () => {

        /* Fields */
        const { stats } = this.state

        /* Check Stats */
        if (!stats) {
            return null
        }

        return (
            <div className="card--summary">

                <div className="card--summary-item">
                    <div className="card--summary-title">
                        <DeckIcon size={12} />
                        {t("card.total")}
                    </div>
                    <div className="card--summary-value">{stats.total}</div>
                </div>

                <div className="card--summary-item">
                    <div className="card--summary-title">
                        <LayersIcon size={12} />
                        {t("card.suits")}
                    </div>
                    <div className="card--summary-value">{this.getSuits().length}</div>
                </div>

                <div className="card--summary-item">
                    <div className="card--summary-title">
                        <CheckIcon size={12} />
                        {t("card.filled")}
                    </div>
                    <div className="card--summary-value">{stats.filled} / {stats.total}</div>
                </div>

                <div className="card--summary-item">
                    <div className="card--summary-title">
                        <WarningIcon size={12} />
                        {t("card.doubles")}
                    </div>
                    <div className={`card--summary-value ${parseInt(stats.doubles) > 0 ? "red" : "green"}`}>{stats.doubles}</div>
                </div>

            </div>
        )
    }

    /* Draw One Row */
    _row = (item, index, doubles) => {

        /* Fields */
        const suit = getSuit(item)
        const state = getState(item, doubles)
        const code = getCode(item)

        return (
            <div onClick={() => this._formModal.open({ item })} className={`card--row ${state}`} key={`${index}`}>

                <div className="card--column card--col-number">{index + 1}</div>

                <div className="card--column card--col-card">

                    <div className={`card--image ${suit}`}>
                        <img src={`${env.mediapoint}cards/${item.image}`} alt={item.name} />
                    </div>

                    <div className="card--text">
                        <div className="card--name">{item.name}</div>
                        <div className="card--note">
                            <span className={`card--rank ${suit}`}>{getRank(item)}</span>
                            <span className={`card--suit ${suit}`}>
                                {this._suit(suit, 11)}
                                {getSuitName(suit)}
                            </span>
                        </div>
                    </div>

                </div>

                <div className="card--column card--col-value">
                    <span className="card--value">{utils.checkValue(item.value, "-")}</span>
                </div>

                <div className="card--column card--col-code">

                    <span className={`card--code ${state}`}>
                        <BarcodeIcon size={12} />
                        {code !== "" ? code : t("card.noBarcode")}
                    </span>

                    {state === "double" &&
                        <span className="card--warning">{t("card.duplicate")}</span>
                    }

                </div>

                <div className="card--column card--col-date">{formatDate(item.updatedAt)}</div>

                <div className="card--column card--col-actions">
                    <div className="card--action" title={t("card.editTitle")}>
                        <EditIcon size={15} />
                    </div>
                </div>

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data } = this.state
        const doubles = getDoubles(data)

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="card--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="card--page">

                {/* Draw Summary */}
                {this._summary()}

                {/* Head */}
                <div className="card--table-header">
                    <div className="card--column card--col-number">#</div>
                    <div className="card--column card--col-card">{t("card.card")}</div>
                    <div className="card--column card--col-value">{t("card.value")}</div>
                    <div className="card--column card--col-code">{t("card.barcode")}</div>
                    <div className="card--column card--col-date">{t("card.changed")}</div>
                    <div className="card--column card--col-actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="card--empty">
                        <BarcodeIcon size={40} />
                        <span>{t("card.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) => this._row(item, index, doubles))}

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { data } = this.state

        return (
            <div className="page">

                <div className="page-header">
                    <h1>{t("card.title")}</h1>
                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Form Modal */}
                <CardForm deck={data} reload={() => this.load()} ref={(ref) => this._formModal = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Cards))
